import { useContext, useState, useEffect } from "react";
import { useMatomo } from "@datapunt/matomo-tracker-react";
import styled from "styled-components";
import { OwcButton, OwcCheckbox, OwcTypography } from "@one/react";
import FilterMainPage from "./FilterMainPage";
import { FilterContext } from "./filters-table/context";
import FilterWrapper from "./FilterWrapper";
import DialogWrapper from "./DialogWrapper";
import { withApollo } from "react-apollo";
import { SHARE_USER_FILTER_AND_COLUMNS } from "../../../gql/bookingapi";
import { snackbarService } from "@one/web-components/dist/index.cjs.js";

const SectionContainer = styled.div`
  border: 1px solid var(--one-color-cobas-gray-300);
  border-radius: 4px;
  margin-bottom: 20px;
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const FilterManagementContent = ({ getBookingItems, client }) => {
  const { trackPageView } = useMatomo();
  const [showMyFilters, setShowMyFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [updateGroupsFn, setUpdateGroupsFn] = useState(null);
  const [triggerFetchFn, setTriggerFetchFn] = useState(null);

  const {
    dialogOpen: isModalOpen,
    setDialogOpen,
    isEditMode,
    setIsEditMode,
    editData,
    setEditData,
  } = useContext(FilterContext);

  useEffect(() => {
    trackPageView({
      documentTitle: "Admin Panel/Group view management",
      href: `${window.location?.origin}/AdminPanel/GroupViewsManagement`,
    });
    // Removed getBookingItems() call - not needed for filter management
    // eslint-disable-next-line
  }, []);

  const handleCheckboxChange = (e) => {
    const newState = e.detail;
    setShowMyFilters(newState);
  };

  const handleAssignFilterClick = () => {
    setIsEditMode(false);
    setEditData(null);
    setDialogOpen(true);
  };

  const handleCloseModal = () => {
    setDialogOpen(false);
    setIsEditMode(false);
    setEditData(null);
  };

  const handleFormSubmit = async (values) => {
    setLoading(true);
    const assignmentIdentifier = Math.random().toString(16).slice(2)
    try {
      await client.mutate({
        mutation: SHARE_USER_FILTER_AND_COLUMNS,
        variables: {
          input: {
            filterId: values.filter ? `EQUIPMENTLIST#${values.filter.filterName}` : null,
            filterUserID: values.filter?.userID ?? null,

            columnSetId: values.columnSet ? `EQUIPMENTLIST#${values.columnSet.displayName}` : null,
            columnSetUserID: values.columnSet?.userID ?? null,

            previousFilterId: values.previousFilter
              ? `EQUIPMENTLIST#${values.previousFilter.filterName}`
              : null,
            previousFilterUserID: values.previousFilter?.userID ?? null,

            previousColumnSetId: values.previousColumnSet
              ? `EQUIPMENTLIST#${values.previousColumnSet.displayName}`
              : null,
            previousColumnSetUserID: values.previousColumnSet?.userID ?? null,

            groupIds: values.groups,
            assignmentIdentifier,
          },
        },
        fetchPolicy: "no-cache",
      });

      snackbarService.show({
        message: "Filter assigned to groups successfully.",
        type: "success",
      });

      handleCloseModal();
      if (updateGroupsFn) {
        updateGroupsFn(`EQUIPMENTLIST#${values.filter.filterName}`, values.groups);
      }
    } catch (error) {
      console.log("Error assigning filter:", error);
      snackbarService.show({
        message: "Failed to assign to group.",
        type: "error",
      });
    } finally {
      setLoading(false);
      if (triggerFetchFn) {
        triggerFetchFn();
      }
    }
  };

  const initialValues = isEditMode && editData
    ? {
      filter: editData.filter,
      columnSet: editData.columnSet,
      groups: editData.groups,
      previousFilter: editData.filter,
      previousColumnSet: editData.columnSet,
    }
    : {
      filter: '',
      columnSet: '',
      groups: [],
      previousFilter: null,
      previousColumnSet: null,
    };
  return (
    <>
      <Header>
        <OwcCheckbox checked={showMyFilters} onInputChange={handleCheckboxChange} label="Show Mine">
          <OwcTypography>Show Mine</OwcTypography>
        </OwcCheckbox>
        <OwcButton variant="primary" onClick={handleAssignFilterClick}>
          Assign group
        </OwcButton>
      </Header>

      <SectionContainer>
        <FilterMainPage
          showMyFilters={showMyFilters}
          registerUpdateFn={(fn) => {
            setUpdateGroupsFn(() => fn);
          }}

          registerTriggerFetch={(fn) => {
            setTriggerFetchFn(() => fn);
          }} />
      </SectionContainer>

      <DialogWrapper
        isModalOpen={isModalOpen}
        handleCloseModal={handleCloseModal}
        handleFormSubmit={handleFormSubmit}
        loading={loading}
        isEditMode={isEditMode}
        initialValues={initialValues}
        client={client}
      />
    </>
  );
};

const FilterManagement = (props) => {
  return (
    <FilterWrapper>
      <FilterManagementContent {...props} />
    </FilterWrapper>
  );
};

export default withApollo(FilterManagement);
